import { useCallback, useEffect, useState } from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState, Pager, Spinner } from "@/components/common";
import { api, cleanParams } from "@/lib/api";
import { fmtDateTime, ROLE_LABEL } from "@/lib/format";
import DateInput from "@/components/DateInput";

const ACTIONS = [["auth", "Sign in / out"], ["bill", "Bill actions"], ["attachment", "Attachments"], ["review", "Reviews"], ["client", "Clients"], ["user", "Users"], ["auditor", "Auditor access"], ["vendor", "Vendors"], ["item", "Items"], ["report", "Exports"], ["settings", "Settings"]];

const dmy = (s) => (s || "").replace(/(\d{4})-(\d{2})-(\d{2})/g, "$3/$2/$1");

export default function AuditLogs() {
  const [f, setF] = useState({ q: "", action: "", date_from: "", date_to: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/audit-logs", { params: { ...cleanParams(f), page, page_size: 50 } }).then(({ data: d }) => setData(d)), [f, page]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const set = (k, v) => { setF((p) => ({ ...p, [k]: v === "all" ? "" : v })); setPage(1); };

  return (
    <div>
      <PageHeader eyebrow="System" title="Audit logs" subtitle="Append-only trail of user activity with Asia/Riyadh timestamps and IP addresses. Entries cannot be edited or deleted." />
      <div className="panel overflow-hidden fade-up">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 px-4 py-3 border-b border-slate-200">
          <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" /><Input value={f.q} onChange={(e) => set("q", e.target.value)} className="pl-9 h-9" placeholder="Search user, details, IP…" data-testid="audit-search-input" /></div>
          <Select value={f.action || "all"} onValueChange={(v) => set("action", v)}>
            <SelectTrigger className="h-9" data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">All activity</SelectItem>{ACTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <DateInput value={f.date_from} onChange={(v) => set("date_from", v)} className="h-9 text-xs" testid="audit-date-from" />
          <DateInput value={f.date_to} onChange={(v) => set("date_to", v)} className="h-9 text-xs" testid="audit-date-to" />
        </div>
        <div className="overflow-x-auto">
          {!data ? <Spinner /> : !data.items.length ? <EmptyState title="No activity" /> : (
            <table className="w-full" data-testid="audit-logs-table">
              <thead><tr>{["Timestamp (AST)", "User", "Role", "Action", "Details", "IP address"].map((h) => <th key={h} className="th">{h}</th>)}</tr></thead>
              <tbody>
                {data.items.map((a, i) => (
                  <tr key={a.id} className="row-hover" data-testid={`audit-row-${i}`}>
                    <td className="td num text-xs whitespace-nowrap">{fmtDateTime(a.at)}</td>
                    <td className="td text-sm font-medium">{a.user_name}</td>
                    <td className="td text-xs text-slate-500">{ROLE_LABEL[a.role] || a.role}</td>
                    <td className="td"><span className="num text-[11px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{a.action}</span></td>
                    <td className="td text-sm text-slate-600 max-w-[420px] truncate" title={dmy(a.details)}>{dmy(a.details)}</td>
                    <td className="td num text-xs text-slate-500">{a.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {data && data.total > 0 && <Pager page={page} pageSize={50} total={data.total} onPage={setPage} />}
      </div>
    </div>
  );
}
